import type { tickets } from '@repletoryreach/sdk-api';

/** Within this of a due time the badge turns amber. */
export const SLA_SOON_MS = 60 * 60_000;

export type SlaKind = 'none' | 'paused' | 'due' | 'overdue';

export interface TicketSlaState {
  kind: SlaKind;
  /** Which promise the badge is about. */
  phase: 'first_response' | 'resolution' | null;
  /** The due time the badge counts to (or from, when overdue). */
  due: string | null;
  /** Time left (`due`) or time over (`overdue`), never negative. */
  ms: number;
  /** Due within {@link SLA_SOON_MS}. */
  soon: boolean;
}

const NONE: TicketSlaState = { kind: 'none', phase: null, due: null, ms: 0, soon: false };

export interface SlaInput {
  sla: tickets.TicketSla | null | undefined;
  status: { category: string };
  /**
   * Known on a ticket's own page; absent on a queue row. Absent, a first-response due time in the past that the
   * server has not marked as breached is taken as met (the breach worker runs every minute).
   */
  first_response_at?: string | null;
}

/**
 * Where a ticket stands against its response targets: paused while pending or on hold, overdue once a due time
 * passes or the server marks a breach, otherwise the nearest due time. Solved and closed tickets have none.
 */
export function ticketSla(input: SlaInput, now: number): TicketSlaState {
  const { sla, status } = input;
  if (!sla || status.category === 'solved' || status.category === 'closed') return NONE;
  if (sla.paused) return { ...NONE, kind: 'paused' };

  const frKnown = input.first_response_at !== undefined;
  const candidates: { phase: 'first_response' | 'resolution'; due: string; at: number }[] = [];
  if (sla.first_response_due && !input.first_response_at) {
    const at = Date.parse(sla.first_response_due);
    if (!Number.isNaN(at) && (at > now || frKnown || sla.breached)) candidates.push({ phase: 'first_response', due: sla.first_response_due, at });
  }
  if (sla.resolution_due) {
    const at = Date.parse(sla.resolution_due);
    if (!Number.isNaN(at)) candidates.push({ phase: 'resolution', due: sla.resolution_due, at });
  }
  candidates.sort((a, b) => a.at - b.at);

  const past = candidates.find((c) => c.at <= now);
  if (past) return { kind: 'overdue', phase: past.phase, due: past.due, ms: now - past.at, soon: false };
  if (sla.breached) {
    const first = candidates[0];
    return { kind: 'overdue', phase: first?.phase ?? null, due: first?.due ?? null, ms: 0, soon: false };
  }
  const next = candidates[0];
  if (!next) return NONE;
  const ms = next.at - now;
  return { kind: 'due', phase: next.phase, due: next.due, ms, soon: ms <= SLA_SOON_MS };
}
